import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type ColumnDef, useTable } from '@tanstack/react-table';

import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { createDataTableFeatures, type DataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { Link } from '@apple/ui/link/Link';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';

import type {
	QueueFilters,
	QueueMessageListing,
	QueueStatus,
} from '@apple/features/queue-message/models';
import { queueFiltersSchema, queueStatusSchema } from '@apple/features/queue-message/models';
import { queueMessageQueries } from '@apple/features/queue-message/queries';

const features = createDataTableFeatures();

export const Route = createFileRoute('/_authed/_admin/manage/queue-message_/$queue_/$status')({
	validateSearch: queueFiltersSchema,

	beforeLoad: ({ params: { status } }) => {
		const queueStatus = queueStatusSchema.parse(status);

		return {
			queueStatus,
		};
	},

	component: RouteComponent,
});

function RouteComponent() {
	const { queue } = Route.useParams();
	const { queueStatus } = Route.useRouteContext();
	const filters = Route.useSearch();

	const navigate = Route.useNavigate();

	const tableState = useTableState<QueueMessageListing, QueueFilters>({
		search: filters,
		navigate,
		defaultFilters: {},
		fieldMap: [],
	});

	const queueMessagesQuery = useQuery(
		queueMessageQueries.getQueueMessages(
			queue,
			queueStatus,
			tableState.state.pagination,
			filters,
		),
	);

	const table = useTable({
		features,
		data: queueMessagesQuery.data.results,
		rowCount: queueMessagesQuery.data.totalCount,
		...tableState,
		manualPagination: true,
		columns: getColumns(queueStatus),
	});

	return (
		<DetailsLayout title={`${queue} - ${queueStatus}`} fluid>
			<DataTable table={table} loading={queueMessagesQuery.isFetching} />
		</DetailsLayout>
	);
}

function getColumns(status: QueueStatus): ColumnDef<DataTableFeatures, QueueMessageListing>[] {
	const commonColumns: ColumnDef<DataTableFeatures, QueueMessageListing>[] = [
		{
			accessorKey: 'messageId',
			header: 'Id',
			cell: ({ row }) => (
				<Link
					to='/manage/queue-message/$messageId'
					params={{
						messageId: row.original.messageId,
					}}
				>
					{row.original.messageId}
				</Link>
			),
		},
		{
			accessorKey: 'queue',
			header: 'Queue Name',
		},
	];

	switch (status) {
		case 'Completed':
			return [
				...commonColumns,
				{
					accessorKey: 'completedOn',
					header: 'Completed Time',
					cell: ({ row }) =>
						row.original.completedOn ? (
							<FormattedDate humanize value={row.original.completedOn} showTime />
						) : null,
				},
				{
					accessorKey: 'deliveryCount',
					header: 'Attempt Count',
				},
			];

		case 'Abandoned':
			return [
				...commonColumns,
				{
					accessorKey: 'abandonedOn',
					header: 'Abandoned Time',
					cell: ({ row }) =>
						row.original.abandonedOn ? (
							<FormattedDate humanize value={row.original.abandonedOn} showTime />
						) : null,
				},
				{
					accessorKey: 'lastAttemptedOn',
					header: 'Last Attempt',
					cell: ({ row }) =>
						row.original.lastAttemptedOn ? (
							<FormattedDate humanize value={row.original.lastAttemptedOn} showTime />
						) : null,
				},
				{
					accessorKey: 'deliveryCount',
					header: 'Attempt Count',
				},
			];

		case 'Scheduled':
			return [
				...commonColumns,
				{
					accessorKey: 'enqueuedOn',
					header: 'Enqueued At',
					cell: ({ row }) => (
						<FormattedDate humanize value={row.original.enqueuedOn} showTime />
					),
				},
				{
					accessorKey: 'enqueueAfter',
					header: 'Scheduled For',
					cell: ({ row }) =>
						row.original.enqueueAfter ? (
							<FormattedDate humanize value={row.original.enqueueAfter} showTime />
						) : null,
				},
				{
					accessorKey: 'deliveryCount',
					header: 'Attempt Count',
				},
			];

		case 'Failed':
			return [
				...commonColumns,
				{
					accessorKey: 'enqueuedOn',
					header: 'Enqueued At',
					cell: ({ row }) => (
						<FormattedDate humanize value={row.original.enqueuedOn} showTime />
					),
				},
				{
					accessorKey: 'lastAttemptedOn',
					header: 'Last Attempt',
					cell: ({ row }) =>
						row.original.lastAttemptedOn ? (
							<FormattedDate humanize value={row.original.lastAttemptedOn} showTime />
						) : null,
				},
				{
					accessorKey: 'deliveryCount',
					header: 'Attempt Count',
				},
			];

		default:
			return [
				...commonColumns,
				{
					accessorKey: 'enqueuedOn',
					header: 'Enqueued At',
					cell: ({ row }) => (
						<FormattedDate humanize value={row.original.enqueuedOn} showTime />
					),
				},
				{
					accessorKey: 'lastAttemptedOn',
					header: 'Last Attempt',
					cell: ({ row }) =>
						row.original.lastAttemptedOn ? (
							<FormattedDate humanize value={row.original.lastAttemptedOn} showTime />
						) : null,
				},
				{
					accessorKey: 'enqueueAfter',
					header: 'Next Attempt',
					cell: ({ row }) =>
						row.original.enqueueAfter ? (
							<FormattedDate humanize value={row.original.enqueueAfter} showTime />
						) : null,
				},
				{
					accessorKey: 'deliveryCount',
					header: 'Attempt Count',
				},
			];
	}
}
